/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Turns a series spec into what
 * the forecast pages show: the cleaned history, the fitted and projected
 * demand, the buyer's adjustments on top, planning levels, dependent demand
 * from panel builds, scope statistics and the scripted explanation.
 */
import { accuracySentence, summarizeAccuracy } from './forecast-accuracy';
import { fmtUnits } from './forecast-format';
import { daysAgo } from '@/demo/procurement/data/dates';
import { displayQty } from '@/demo/procurement/data/format';
import type { SupplierId } from '@/demo/procurement/data/types';
import { getPerson, PERSONA_ID } from '@/demo/procurement/data/company';
import {
  AXIS,
  BOM_LINES,
  FG_PLAN,
  HISTORY_MONTHS,
  OUTLIER_RULES,
  PANEL_SHOP_SITE,
  SERIES_SPECS,
  bomForComponent,
  bomForFinishedGood,
  getRule,
  getSpec,
  isFinishedGoodSku,
  rawHistoryFor,
  seriesIdOf,
  specId,
} from './forecast-data';
import {
  adjustForecast,
  applyOutlierRule,
  averageDemandSize,
  chooseMethod,
  forecastBand,
  holtWintersFit,
  hitsInWindow,
  median,
  movingAverageFit,
  naiveScale,
  planningLevels,
  profileDemand,
  residualSigma,
  scoreMonth,
  seasonalStrength,
  sum,
  tsbFit,
  type FitResult,
} from './forecast-math';
import type {
  AccuracyRow,
  ApprovalRecord,
  FactorOverride,
  ForecastDemoState,
  ForecastLevels,
  ForecastMethod,
  ForecastView,
  HistoryChange,
  HistoryEdit,
  HistoryReason,
  Measure,
  MonthKey,
  SeriesResult,
  SeriesRule,
  SeriesSpec,
  SpikeFlag,
} from './forecast-types';

/** Closed months scored against what was forecast. */
export const SCORED_MONTHS = 6;

// --- Outlier rule resolution --------------------------------------------------------

/**
 * A series follows exactly one rule: the most specific enabled rule that
 * reaches it. Rules do not stack.
 */
export function governingRule(
  spec: SeriesSpec,
  ruleEnabled: Record<string, boolean>,
): SeriesRule | null {
  const id = specId(spec);
  const enabled = OUTLIER_RULES.filter(
    (rule) => ruleEnabled[rule.id] !== false,
  );
  const itemRule = enabled.find(
    (rule) => rule.scope === 'item_loc' && rule.seriesIds.includes(id),
  );
  if (itemRule) return itemRule;
  return enabled.find((rule) => rule.scope === 'organization') ?? null;
}

// --- The fit ----------------------------------------------------------------------------

const RESULT_CACHE = new Map<string, SeriesResult>();

function seasonalByCalendarMonth(seasonal: number[]): number[] {
  const firstMonth = AXIS.history[0]?.monthIndex ?? 0;
  return Array.from({ length: 12 }, (_, month) => {
    const slot = (month - firstMonth + 12) % 12;
    return seasonal[slot] ?? 1;
  });
}

interface MethodFit {
  fit: FitResult;
  seasonalByMonth: number[] | null;
}

function fitMethod(
  method: ForecastMethod,
  history: readonly number[],
  horizon: number,
): MethodFit {
  switch (method) {
    case 'holt_winters': {
      const fit = holtWintersFit(history, horizon);
      return { fit, seasonalByMonth: seasonalByCalendarMonth(fit.seasonal) };
    }
    case 'tsb':
      return { fit: tsbFit(history, horizon), seasonalByMonth: null };
    case 'moving_avg':
      return { fit: movingAverageFit(history, horizon), seasonalByMonth: null };
  }
}

/** Months a trailing average reads when a pinned forecast has to react to a spike. */
const SPIKE_WINDOW_MONTHS = 12;

/**
 * A pinned model output assumes its spike was left out. When the history the
 * forecaster reads still carries the spike (a buyer has not left it out, or the
 * rule is switched off), the pinned numbers rise by what a trailing average
 * over the year would pick up, so the forecast moves the way it would on a
 * fitted series.
 */
function scalePinnedForecast(
  spec: SeriesSpec,
  raw: readonly number[],
  cleaned: readonly number[],
  pinned: readonly number[],
): number[] {
  if (!spec.spike) return [...pinned];
  const itemRule = OUTLIER_RULES.find(
    (rule) =>
      rule.scope === 'item_loc' && rule.seriesIds.includes(specId(spec)),
  );
  if (!itemRule) return [...pinned];
  const reference = applyOutlierRule(raw, {
    ...itemRule.params,
    action: 'exclude',
  }).adjusted;
  const current =
    movingAverageFit(cleaned, 1, SPIKE_WINDOW_MONTHS).forecast[0] ?? 0;
  const without =
    movingAverageFit(reference, 1, SPIKE_WINDOW_MONTHS).forecast[0] ?? 0;
  const lift = without > 0 ? current / without : 1;
  return pinned.map((value) => value * lift);
}

const NO_EDITS: Readonly<Record<string, HistoryEdit>> = {};

function editsKey(edits: Readonly<Record<string, HistoryEdit>>): string {
  return Object.entries(edits)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(
      ([key, edit]) => `${key}=${edit.value}:${edit.reason}:${edit.note ?? ''}`,
    )
    .join(',');
}

/** Lays a buyer's corrections over the history the rule produced. */
function applyHistoryEdits(
  base: readonly number[],
  raw: readonly number[],
  edits: Readonly<Record<string, HistoryEdit>>,
): { values: number[]; changes: HistoryChange[] } {
  const values = [...base];
  const changes: HistoryChange[] = [];
  for (const [monthKey, edit] of Object.entries(edits)) {
    const index = AXIS.history.findIndex((month) => month.key === monthKey);
    const month = AXIS.history[index];
    if (index < 0 || !month) continue;
    const value = Math.max(0, Math.round(edit.value));
    const original = raw[index] ?? 0;
    if (value === original) continue;
    values[index] = value;
    changes.push({ ...edit, value, index, month, original });
  }
  changes.sort((a, b) => a.index - b.index);
  return { values, changes };
}

/** The corrections already on a result, keyed by month, ready to edit and re-run. */
function editsOf(result: SeriesResult): Record<string, HistoryEdit> {
  const edits: Record<string, HistoryEdit> = {};
  for (const change of result.edits) {
    edits[change.month.key] = {
      value: change.value,
      reason: change.reason,
      ...(change.note === undefined ? {} : { note: change.note }),
    };
  }
  return edits;
}

/**
 * Runs the pipeline for one series: outlier rule, demand class, method routing,
 * fit, band, and the last six months scored against what was forecast. Results
 * are cached by series and governing rule, since everything else is a function
 * of those two.
 */
export function computeSeries(
  spec: SeriesSpec,
  ruleId: string | null,
  edits: Readonly<Record<string, HistoryEdit>> = NO_EDITS,
): SeriesResult {
  const id = specId(spec);
  const cacheKey = `${id}|${ruleId ?? 'none'}|${editsKey(edits)}`;
  const cached = RESULT_CACHE.get(cacheKey);
  if (cached) return cached;

  const raw = rawHistoryFor(spec);
  const rule = ruleId ? getRule(ruleId) : undefined;
  const outlier = rule
    ? applyOutlierRule(raw, rule.params)
    : { adjusted: [...raw], adjustments: [], flagged: [] };
  const corrected = applyHistoryEdits(outlier.adjusted, raw, edits);
  const cleaned = corrected.values;
  const decided = new Set(corrected.changes.map((change) => change.index));
  const flags: SpikeFlag[] = outlier.flagged.flatMap((flag) => {
    const month = AXIS.history[flag.index];
    return month && rule && !decided.has(flag.index)
      ? [
          {
            index: flag.index,
            month,
            original: flag.original,
            suggested: flag.adjusted,
            ruleId: rule.id,
          },
        ]
      : [];
  });
  const profile = profileDemand(cleaned);
  const seasonality = seasonalStrength(cleaned);
  // A pinned model output reads as a trended seasonal fit.
  const method: ForecastMethod = spec.modelForecast
    ? 'holt_winters'
    : chooseMethod(profile.demandClass, seasonality);
  const { fit, seasonalByMonth } = fitMethod(
    method,
    cleaned,
    AXIS.forecast.length,
  );
  const forecast = (
    spec.modelForecast
      ? scalePinnedForecast(spec, raw, cleaned, spec.modelForecast)
      : fit.forecast
  ).map((value) => Math.round(value));
  const sigma = residualSigma(cleaned, fit.fitted);
  const band = forecastBand(forecast, sigma);
  const scale = naiveScale(cleaned);
  const accuracy: AccuracyRow[] = [];
  for (let i = HISTORY_MONTHS - SCORED_MONTHS; i < HISTORY_MONTHS; i += 1) {
    const month = AXIS.history[i];
    if (!month) continue;
    accuracy.push({
      month,
      ...scoreMonth(Math.round(fit.fitted[i] ?? 0), raw[i] ?? 0, scale),
    });
  }

  const result: SeriesResult = {
    id,
    spec,
    axis: AXIS,
    raw,
    cleaned,
    adjustments: outlier.adjustments,
    edits: corrected.changes,
    flags,
    governingRuleId: rule?.id ?? null,
    fitted: fit.fitted.map((value) => Math.round(value)),
    forecast,
    lower: band.lower,
    upper: band.upper,
    method,
    demandClass: profile.demandClass,
    adi: profile.adi,
    cv2: profile.cv2,
    seasonality,
    seasonalByMonth,
    sigma,
    hits6: hitsInWindow(raw, 6),
    hits9: hitsInWindow(raw, 9),
    hits12: hitsInWindow(raw, 12),
    priorYearHits: hitsInWindow(raw, 12, 12),
    avgDemandSize: averageDemandSize(cleaned),
    accuracy,
  };
  RESULT_CACHE.set(cacheKey, result);
  return result;
}

// --- State and views ---------------------------------------------------------------------

/**
 * The note on a spec may carry `{bomGap}`: how far the statistical forecast
 * sits from what the panel build plan implies, as '13% under' or '4% over'. Computed, not typed, so it
 * cannot drift from the numbers on the page.
 */
function resolveNote(spec: SeriesSpec): string {
  const { note } = spec.approval;
  if (!note.includes('{bomGap}')) return note;
  const result = computeSeries(spec, governingRule(spec, {})?.id ?? null);
  const implied = dependentDemand(spec.sku).impliedTotal;
  const statistical = sum(result.forecast.slice(0, DEPENDENT_MONTHS));
  const gap = implied === 0 ? 0 : ((implied - statistical) / implied) * 100;
  const rounded = Math.round(Math.abs(gap));
  const phrase =
    rounded === 0 ? 'level with' : `${rounded}% ${gap > 0 ? 'under' : 'over'}`;
  return note.replace('{bomGap}', phrase);
}

function approvalSeed(spec: SeriesSpec): ApprovalRecord {
  const { status, decidedDaysAgo } = spec.approval;
  const note = resolveNote(spec);
  if (decidedDaysAgo === null) {
    return { status, note, decidedBy: null, decidedAt: null };
  }
  return {
    status,
    note,
    decidedBy: 'Auto-approved',
    decidedAt: daysAgo(decidedDaysAgo).toISOString(),
  };
}

export function seedForecastState(): ForecastDemoState {
  const approvals: Record<string, ApprovalRecord> = {};
  const factors: Record<string, FactorOverride> = {};
  const audit: ForecastDemoState['audit'] = [];
  const ruleEnabled: Record<string, boolean> = {};
  for (const rule of OUTLIER_RULES) ruleEnabled[rule.id] = true;
  for (const spec of SERIES_SPECS) {
    const id = specId(spec);
    approvals[id] = approvalSeed(spec);
    if (spec.seedFactor) {
      factors[id] = {
        factor: spec.seedFactor.factor,
        fromIndex: spec.seedFactor.fromIndex,
        reason: spec.seedFactor.reason,
      };
      audit.push({
        id: `seed-factor-${id}`,
        seriesId: id,
        at: daysAgo(spec.seedFactor.addedDaysAgo).toISOString(),
        by: getPerson(PERSONA_ID).name,
        text: `Added a growth adjustment of ${formatFactorPercent(spec.seedFactor.factor)}: ${spec.seedFactor.reason}`,
      });
    }
  }
  for (const rule of OUTLIER_RULES) {
    for (const seriesId of rule.seriesIds) {
      audit.push({
        id: `seed-rule-${rule.id}-${seriesId}`,
        seriesId,
        at: daysAgo(rule.createdDaysAgo).toISOString(),
        by: rule.createdBy,
        text: `Spike rule ${rule.id} created: ${rule.description}`,
      });
    }
  }
  return { ruleEnabled, approvals, factors, pins: {}, historyEdits: {}, audit };
}

/** `1.08` reads as `+8%`, `0.9` as `-10%`. */
export function formatFactorPercent(factor: number): string {
  const pct = Math.round((factor - 1) * 1000) / 10;
  return `${pct > 0 ? '+' : ''}${pct}%`;
}

function levelsFor(
  monthlyDemand: readonly number[],
  result: SeriesResult,
): ForecastLevels {
  const { leadTime, targetAvailability } = result.spec;
  const levels = planningLevels({
    monthlyDemand,
    sigma: result.sigma,
    leadTimeDays: leadTime.days,
    targetAvailability,
  });
  return {
    min: levels.min,
    max: levels.max,
    safetyStock: levels.safetyStock,
    leadTimeDemand: levels.leadTimeDemand,
  };
}

/** The forecast after the buyer's growth change and fixed months. */
function adjustFor(
  result: SeriesResult,
  factor: FactorOverride | null,
  pins: Record<number, number>,
): number[] {
  return adjustForecast(result.forecast, {
    factor: factor?.factor ?? 1,
    fromIndex: factor?.fromIndex ?? 0,
    ...(factor?.toIndex === undefined ? {} : { toIndex: factor.toIndex }),
    pins: new Map(Object.entries(pins).map(([k, v]) => [Number(k), v])),
  });
}

export function buildView(
  spec: SeriesSpec,
  state: ForecastDemoState,
): ForecastView {
  const id = specId(spec);
  const rule = governingRule(spec, state.ruleEnabled);
  const ruleId = rule?.id ?? null;
  const result = computeSeries(
    spec,
    ruleId,
    state.historyEdits[id] ?? NO_EDITS,
  );
  const uncorrected =
    result.edits.length > 0 ? computeSeries(spec, ruleId) : null;
  const factor = state.factors[id] ?? null;
  const pinsByKey = state.pins[id] ?? {};
  const pins: Record<number, number> = {};
  result.axis.forecast.forEach((month, index) => {
    const pinned = pinsByKey[month.key];
    if (pinned !== undefined) pins[index] = pinned;
  });
  const adjusted = adjustFor(result, factor, pins);
  const approval: ApprovalRecord = state.approvals[id] ?? approvalSeed(spec);
  const base = uncorrected ?? result;
  return {
    id,
    spec,
    result,
    uncorrected,
    approval,
    factor,
    pins,
    adjusted,
    baseLevels: levelsFor(base.forecast, base),
    levels: levelsFor(adjusted, result),
    hasOverride: factor !== null || Object.keys(pins).length > 0,
  };
}

export function buildViews(state: ForecastDemoState): ForecastView[] {
  return SERIES_SPECS.map((spec) => buildView(spec, state));
}

// --- Scope statistics --------------------------------------------------------------------

/** Quantities read in display units (wire in feet); cost follows the data unit. */
function measureFactor(view: ForecastView, measure: Measure): number {
  return measure === 'cost'
    ? view.spec.unitCost
    : displayQty(1, view.spec.unit);
}

export function viewTotal(
  view: ForecastView,
  months: number,
  measure: Measure,
): number {
  return sum(view.adjusted.slice(0, months)) * measureFactor(view, measure);
}

/** Per forecast month, summed across the views. */
export function monthTotals(
  views: readonly ForecastView[],
  months: number,
  measure: Measure,
): number[] {
  return Array.from({ length: months }, (_, index) =>
    sum(
      views.map(
        (view) => (view.adjusted[index] ?? 0) * measureFactor(view, measure),
      ),
    ),
  );
}

export interface ScopeStats {
  seriesCount: number;
  skuCount: number;
  /** Sites in scope. A units total would add wire feet to fixtures and drives. */
  locationCount: number;
  projectedSpend: number;
  /** Month with the highest projected spend across the horizon. */
  peakMonth: MonthKey | null;
  /** Share of series on an assumed lead time, 0..1. */
  assumedLeadTimeShare: number;
  needsApproval: number;
  overrides: number;
  medianScaledError: number | null;
}

export function scopeStats(
  views: readonly ForecastView[],
  months = 6,
): ScopeStats {
  const spendByMonth = monthTotals(views, AXIS.forecast.length, 'cost');
  const peakIndex = spendByMonth.reduce(
    (best, value, index) => (value > (spendByMonth[best] ?? 0) ? index : best),
    0,
  );
  const scaled = views
    .flatMap((view) => view.result.accuracy)
    .map((row) => row.scaledError)
    .filter((value): value is number => value !== null);
  return {
    seriesCount: views.length,
    skuCount: new Set(views.map((view) => view.spec.sku)).size,
    locationCount: new Set(views.map((view) => view.spec.siteId)).size,
    projectedSpend: sum(views.map((view) => viewTotal(view, months, 'cost'))),
    peakMonth: views.length === 0 ? null : (AXIS.forecast[peakIndex] ?? null),
    assumedLeadTimeShare:
      views.length === 0
        ? 0
        : views.filter((view) => view.spec.leadTime.source === 'assumed')
            .length / views.length,
    needsApproval: views.filter(
      (view) => view.approval.status === 'needs_approval',
    ).length,
    overrides: views.filter((view) => view.hasOverride).length,
    medianScaledError: scaled.length === 0 ? null : median(scaled),
  };
}

// --- Dependent demand from panel builds -------------------------------------------------------

export const DEPENDENT_MONTHS = 6;

export interface DependentLine {
  finishedGood: string;
  qtyPer: number;
  /** Panels (or kits) planned per month. */
  plan: number[];
  /** Component units those builds need per month. */
  implied: number[];
}

export interface DependentDemand {
  lines: DependentLine[];
  /** Component units needed per month across all finished goods. */
  impliedByMonth: number[];
  impliedTotal: number;
}

/**
 * Explodes the finished-goods plan through the bills of materials into
 * component units, month by month. `planFor` supplies the build plan so the
 * caller can use the adjusted forecast or the canonical one.
 */
export function dependentDemand(
  componentSku: string,
  planFor: (finishedGood: string) => number[] = (sku) =>
    isFinishedGoodSku(sku) ? FG_PLAN[sku] : [],
): DependentDemand {
  const lines = bomForComponent(componentSku).map((bom): DependentLine => {
    const plan = planFor(bom.finishedGood).slice(0, DEPENDENT_MONTHS);
    return {
      finishedGood: bom.finishedGood,
      qtyPer: bom.qtyPer,
      plan,
      implied: plan.map((units) => units * bom.qtyPer),
    };
  });
  const impliedByMonth = Array.from({ length: DEPENDENT_MONTHS }, (_, index) =>
    sum(lines.map((line) => line.implied[index] ?? 0)),
  );
  return { lines, impliedByMonth, impliedTotal: sum(impliedByMonth) };
}

export function isBomComponent(sku: string): boolean {
  return BOM_LINES.some((line) => line.component === sku);
}

export interface BomRow {
  component: string;
  qtyPer: number;
  /** The component's own series at the panel shop, when there is one. */
  seriesId: string | null;
  description: string;
  /** Unit the component's quantities are kept in ('C' for wire). */
  unit: string;
  supplierIds: SupplierId[];
  leadTimeDays: number | null;
  /** Component units needed for the next six months of builds. */
  sixMonthNeed: number;
}

export function bomRows(finishedGood: string, plan: number[]): BomRow[] {
  const planTotal = sum(plan.slice(0, DEPENDENT_MONTHS));
  return bomForFinishedGood(finishedGood).map((line) => {
    const componentSpec = getSpec(seriesIdOf(line.component, PANEL_SHOP_SITE));
    return {
      component: line.component,
      qtyPer: line.qtyPer,
      seriesId: componentSpec ? specId(componentSpec) : null,
      description: componentSpec?.description ?? line.component,
      unit: componentSpec?.unit ?? 'each',
      supplierIds: componentSpec?.supplierIds ?? [],
      leadTimeDays: componentSpec?.leadTime.days ?? null,
      sixMonthNeed: planTotal * line.qtyPer,
    };
  });
}

/** Pins that make the adjusted forecast follow the panel build plan. */
export function bomPins(implied: readonly number[]): Record<string, number> {
  const pins: Record<string, number> = {};
  implied.forEach((value, index) => {
    const month = AXIS.forecast[index];
    if (month) pins[month.key] = Math.round(value);
  });
  return pins;
}

// --- Spike decision -------------------------------------------------------------------------

export const HISTORY_REASON_LABEL: Record<HistoryReason, string> = {
  one_time_order: 'One-time order',
  out_of_stock: 'Ran out of stock',
  data_error: 'Data error',
  other: 'Other reason',
};

export const HISTORY_REASONS: readonly HistoryReason[] = [
  'one_time_order',
  'out_of_stock',
  'data_error',
  'other',
];

/** The reason and note the spike action records on the month it leaves out. */
export const SPIKE_REASON: HistoryReason = 'one_time_order';
export const SPIKE_NOTE = 'Riverside Stadium project';

/** One decision about a spike, with what it does to the forecast either way. */
export interface SpikeDecision {
  monthKey: string;
  month: MonthKey;
  /** Recorded demand in the spike month. */
  original: number;
  /** The typical month it is read as when left out. */
  typical: number;
  /** Original over typical, such as 3.1 times. */
  multiple: number;
  /** True while the spike still counts as recorded demand. */
  counted: boolean;
  /** What the forecast reads for the month right now. */
  readAs: number;
  ruleId: string;
  /** 12-month model forecast with the spike counted. */
  totalCounted: number;
  /** 12-month model forecast with the spike left out. */
  totalLeftOut: number;
  /** Left in, the forecast rises by this many percent over leaving it out. */
  liftPercent: number;
  /** Leaving it out lowers the forecast by this many percent from counting it. */
  dropPercent: number;
  /** Planning levels with the spike counted and with it left out. */
  levelsCounted: ForecastLevels;
  levelsLeftOut: ForecastLevels;
}

/**
 * The spike a flag rule found on a series, or one a buyer already left out,
 * with the effect of each choice. Null when the series has no such spike.
 */
export function spikeDecision(view: ForecastView): SpikeDecision | null {
  const { result } = view;
  const flag = result.flags[0] ?? view.uncorrected?.flags[0];
  if (!flag) return null;
  const monthKey = flag.month.key;
  const counted = result.flags.some((item) => item.index === flag.index);
  const run = (edit: HistoryEdit | null): SeriesResult => {
    const edits = editsOf(result);
    if (edit) edits[monthKey] = edit;
    else delete edits[monthKey];
    return computeSeries(view.spec, result.governingRuleId, edits);
  };
  const leaveOut: HistoryEdit = {
    value: flag.suggested,
    reason: SPIKE_REASON,
    note: SPIKE_NOTE,
  };
  const left = counted ? run(leaveOut) : result;
  const kept = counted ? result : run(null);
  const totalCounted = sum(kept.forecast);
  const totalLeftOut = sum(left.forecast);
  const levels = (series: SeriesResult): ForecastLevels =>
    levelsFor(adjustFor(series, view.factor, view.pins), series);
  return {
    monthKey,
    month: flag.month,
    original: flag.original,
    typical: flag.suggested,
    multiple: flag.original / Math.max(1, flag.suggested),
    counted,
    readAs: counted
      ? flag.original
      : (result.edits.find((change) => change.index === flag.index)?.value ??
        flag.original),
    ruleId: flag.ruleId,
    totalCounted,
    totalLeftOut,
    liftPercent:
      totalLeftOut === 0
        ? 0
        : ((totalCounted - totalLeftOut) / totalLeftOut) * 100,
    dropPercent:
      totalCounted === 0
        ? 0
        : ((totalCounted - totalLeftOut) / totalCounted) * 100,
    levelsCounted: levels(kept),
    levelsLeftOut: levels(left),
  };
}

/** The history edit that leaves a flagged spike out of the forecast. */
export function spikeEdit(decision: SpikeDecision): HistoryEdit {
  return {
    value: decision.typical,
    reason: SPIKE_REASON,
    note: SPIKE_NOTE,
  };
}

// --- Scripted explanation -----------------------------------------------------------------------

export const METHOD_LABEL: Record<ForecastMethod, string> = {
  tsb: 'Occasional demand',
  moving_avg: 'Moving average',
  holt_winters: 'Seasonal trend',
};

export const CLASS_LABEL: Record<SeriesResult['demandClass'], string> = {
  smooth: 'Steady',
  erratic: 'Variable',
  intermittent: 'Occasional',
  lumpy: 'Sporadic',
};

const METHOD_REASON: Record<ForecastMethod, string> = {
  holt_winters:
    'steady demand with a repeating yearly shape gets trend plus seasonality',
  moving_avg:
    'steady or variable demand without a strong seasonal signal gets a moving average',
  tsb: 'occasional and sporadic demand gets a method that tracks how often demand shows up separately from how big it is',
};

export interface Explanation {
  headline: string;
  steps: { label: string; detail: string }[];
  points: string[];
}

export function explainView(view: ForecastView): Explanation {
  const { result, spec } = view;
  const total = sum(view.adjusted);
  const statistical = sum(result.forecast);
  const peakIndex = view.adjusted.reduce(
    (best, value, index) => (value > (view.adjusted[best] ?? 0) ? index : best),
    0,
  );
  const peakMonth = result.axis.forecast[peakIndex];
  const adjustment = result.adjustments[0];
  const spikeMonth = adjustment
    ? result.axis.history[adjustment.index]
    : undefined;
  const rule = result.governingRuleId
    ? getRule(result.governingRuleId)
    : undefined;
  const decision = spikeDecision(view);
  const change = result.edits[0];
  const spikeStep = (() => {
    if (decision && decision.counted) {
      return {
        label: 'Spotting a spike',
        detail: `${decision.month.label}: ${fmtUnits(decision.original, spec.unit)} is ${decision.multiple.toFixed(1)} times a typical month, so ${decision.ruleId} flagged it. It still counts`,
      };
    }
    if (decision) {
      return {
        label: 'Leaving out a spike',
        detail: `${decision.month.label}: ${fmtUnits(decision.original, spec.unit)} read as ${fmtUnits(decision.readAs, spec.unit)}`,
      };
    }
    if (change) {
      return {
        label: 'Using corrected history',
        detail: `${change.month.label}: ${fmtUnits(change.original, spec.unit)} read as ${fmtUnits(change.value, spec.unit)}, ${HISTORY_REASON_LABEL[change.reason].toLowerCase()}`,
      };
    }
    if (adjustment && rule && spikeMonth) {
      return {
        label: 'Leaving out a spike',
        detail: `${spikeMonth.label}: ${fmtUnits(adjustment.original, spec.unit)} read as ${fmtUnits(adjustment.adjusted, spec.unit)} under ${rule.id}`,
      };
    }
    return {
      label: 'Checking spike rules',
      detail: rule
        ? `No month was big enough to count as a spike under ${rule.id}`
        : 'No spike rule applies to this item',
    };
  })();

  const steps = [
    {
      label: 'Reading past demand',
      detail: `${result.axis.history.length} past months, ${result.hits12} of the last 12 had demand`,
    },
    spikeStep,
    {
      label: 'Choosing a method',
      detail: `${CLASS_LABEL[result.demandClass]} demand (${result.adi.toFixed(2)} months between orders, variability ${result.cv2.toFixed(2)}): ${METHOD_LABEL[result.method]}`,
    },
    {
      label: 'Applying manual changes',
      detail: view.factor
        ? `Growth adjustment ${formatFactorPercent(view.factor.factor)}${Object.keys(view.pins).length > 0 ? ` and ${Object.keys(view.pins).length} fixed months` : ''}`
        : Object.keys(view.pins).length > 0
          ? `${Object.keys(view.pins).length} fixed months`
          : 'No manual changes on this item',
    },
  ];

  const points: string[] = [];
  if (result.method === 'holt_winters' && result.seasonalByMonth) {
    const peak = result.seasonalByMonth.reduce(
      (best, value, index) =>
        value > (result.seasonalByMonth?.[best] ?? 0) ? index : best,
      0,
    );
    const peakShort = result.axis.forecast.find(
      (month) => month.monthIndex === peak,
    )?.short;
    points.push(
      `Seasonality follows the construction season${peakShort ? `, with the yearly peak in ${peakShort}` : ''}.`,
    );
  }
  if (decision) {
    points.push(
      decision.counted
        ? `${decision.month.label} was ${fmtUnits(decision.original, spec.unit)}, about ${decision.multiple.toFixed(1)} times a normal month (the ${SPIKE_NOTE}). It still counts, so it lifts the 12-month forecast by ${decision.liftPercent.toFixed(0)}%. Leaving it out would read it as ${fmtUnits(decision.typical, spec.unit)}.`
        : `${decision.month.label} was ${fmtUnits(decision.original, spec.unit)}, about ${decision.multiple.toFixed(1)} times a normal month (the ${SPIKE_NOTE}). Left out and read as ${fmtUnits(decision.readAs, spec.unit)}, it keeps the 12-month forecast ${decision.dropPercent.toFixed(0)}% lower.`,
    );
  } else if (adjustment && rule && spikeMonth) {
    points.push(
      `${spikeMonth.label} was ${fmtUnits(adjustment.original, spec.unit)}, about ${(adjustment.original / Math.max(1, adjustment.adjusted)).toFixed(1)} times a normal month, so ${rule.id} read it as ${fmtUnits(adjustment.adjusted, spec.unit)}.`,
    );
  }
  const otherEdits = result.edits.filter(
    (item) => item.month.key !== decision?.monthKey,
  );
  if (otherEdits.length > 0) {
    points.push(
      `${otherEdits.length === 1 ? 'One past month was' : `${otherEdits.length} past months were`} corrected before forecasting: ${otherEdits
        .map(
          (item) =>
            `${item.month.label} from ${fmtUnits(item.original, spec.unit)} to ${fmtUnits(item.value, spec.unit)} (${HISTORY_REASON_LABEL[item.reason].toLowerCase()})`,
        )
        .join(', ')}.`,
    );
  }
  if (view.factor) {
    points.push(
      `A ${formatFactorPercent(view.factor.factor)} growth adjustment${view.factor.toIndex === undefined ? '' : ` for ${Math.max(1, view.factor.toIndex - view.factor.fromIndex)} months`} adds ${fmtUnits(total - statistical, spec.unit)} to the 12-month forecast. ${view.factor.reason}.`,
    );
  }
  // The same numbers as the Forecast accuracy section on the details page.
  const accuracy = accuracySentence(summarizeAccuracy(result));
  if (accuracy) points.push(accuracy);
  points.push(
    spec.leadTime.source === 'measured'
      ? `Delivery time is measured at ${spec.leadTime.days} days from ${spec.leadTime.samples} deliveries.`
      : `Delivery time is an estimated ${spec.leadTime.days} days because there are not enough deliveries to measure it.`,
  );
  if (spec.story) points.push(spec.story);

  return {
    headline: `${fmtUnits(total, spec.unit)} over 12 months${peakMonth ? `, peaking in ${peakMonth.short}` : ''}`,
    steps,
    points,
  };
}
